<template>
    <div class="modal fade" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-body text-center ">
                    <div v-if="!showConfirmationScreen">
                        <div>
                            <h1 class="mx-3">{{ modalTitle }}</h1> 
                        </div> 
                        <i class="fas fa-exclamation-circle my-3 mb-5 fa-5x text-danger"></i>
                        <p v-if="itemType === eRemoveItemType.AdminAccess">
                            {{ `${this.itemToRemove.firstName} ${this.itemToRemove.lastName}` }} is currently set an administrator. By removing this status {{ `${this.itemToRemove.firstName}` }} will no longer have the ability to manage users on the account.
                        </p>
                        <p v-else-if="itemType === eRemoveItemType.User">
                            By clicking Confirm, you will send a request to OnlineFuels to delete {{ `${itemToRemove.firstName} ${itemToRemove.lastName}` }}'s information. Are you sure you want to continue?
                        </p>
                        <p v-else-if="itemType === eRemoveItemType.ActiveUser">
                            Are you sure you want to disable the user {{ `${itemToRemove.firstName} ${itemToRemove.lastName}` }}?
                        </p>
                        <p v-else-if="itemType === eRemoveItemType.UnauthorisedAction">
                            You’re not authorised to perform this action. Your permissions may have changed.
                        </p>
                        <p v-else-if="itemType === eRemoveItemType.UnconfirmedChanges">
                            Unsaved changes will be lost if you leave this page.
                        </p>
                        <div v-else class="table-container">
                            <table class="table">
                                <thead>
                                    <tr v-if="itemType === eRemoveItemType.Location">
                                        <th label="location">Delivery Location</th>
                                        <th label="postcode">Postcode</th>
                                    </tr>
                                    <tr v-else-if="itemType === eRemoveItemType.Rule" class="heading-medium">
                                        <th label="Name">Name</th>
                                        <th label="UoM">UoM</th>
                                        <th label="Value">Value</th>
                                    </tr>
                                    <tr v-else>
                                        <th label="buyer" v-if="itemType === eRemoveItemType.Offer && itemToRemove.isContract">Buyer</th>
                                        <th label="fuel">Product</th>
                                        <th label="terminal" v-if="(itemType === eRemoveItemType.Offer && itemToRemove.storage_type_id === 'SPOT') || (itemType === eRemoveItemType.Negotiation && itemToRemove.terminal)">Terminal</th>
                                        <th label="network" v-else-if="(itemType === eRemoveItemType.Offer && itemToRemove.storage_type_id === 'BUNKERING') || (itemType === eRemoveItemType.Negotiation && !itemToRemove.terminal)">Network</th>
                                        <th label="volume" v-if="itemType === eRemoveItemType.Negotiation">Vol/L</th>
                                        <th label="premium" v-else-if="itemType === eRemoveItemType.Offer">Premium(ppl)</th>
                                        <th label="price">Price(ppl)</th>
                                    </tr>
                                </thead> 
                                <tbody>
                                    <tr v-if="itemType === eRemoveItemType.Location">
                                        <td label="location">{{ itemToRemove.name }}</td>
                                        <td label="postcode">{{ itemToRemove.address.postCode }}</td>
                                    </tr>
                                    <tr v-else-if="itemType === eRemoveItemType.Rule" class="heading-medium">
                                        <td label="Name">
                                            <span v-if="itemToRemove.name">{{ itemToRemove.name }}</span>
                                            <span v-else>&mdash;</span>
                                        </td>
                                        <td label="UoM">
                                            <span v-if="itemToRemove.action.unitOfMeasurement != -1">{{ getUnitDisplay(itemToRemove.action.unitOfMeasurement) }}</span>
                                            <span v-else>&mdash;</span>
                                        </td>
                                        <td label="Value">
                                            <span v-if="itemToRemove.action.value >= 0">{{ itemToRemove.action.value | priceformat }}</span>
                                            <span v-else>&mdash;</span>
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td label="buyer" v-if="itemType === eRemoveItemType.Offer && itemToRemove.isContract && itemToRemove.contract.buyer">{{ itemToRemove.contract.buyer.name }}</td>
                                        <td label="fuel" v-if="itemToRemove.terminal">{{ this.itemToRemove.fuel_id | fueltype }}</td>
                                        <td label="fuel" v-else>Network</td>
                                        <td label="terminal" v-if="itemToRemove.terminal">{{ itemToRemove.terminal.name }}</td>
                                        <td label="network" v-else>{{ getNetwork() }}</td>
                                        <td label="volume" v-if="itemType === eRemoveItemType.Negotiation">{{ itemToRemove.quantity | volumeformat }}</td>
                                        <td label="premium" v-else-if="itemType === eRemoveItemType.Offer">
                                            <span v-if="itemToRemove.type_id === 'FIXED'">&mdash;</span>
                                            <span v-else>{{ (isPricingEngineEnabled ? itemToRemove.premium : itemToRemove.price) | priceformat }}</span>
                                        </td> 
                                        <td label="price">{{ (itemType === eRemoveItemType.Offer && !isPricingEngineEnabled || itemType === eRemoveItemType.Negotiation && itemToRemove.type_id === "TRACKING" ? itemToRemove.livePrice : itemToRemove.price) | priceformat }}</td>
                                    </tr>
                                </tbody> 
                            </table> 
                        </div>
                        <p v-if="itemType === eRemoveItemType.Rule">
                            This will be removed across all applicable offers
                        </p>
                    </div>
                </div>
                <div class="modal-footer justify-content-center">
                  <button class="btn btn-tertiary mr-2" id="cancelButton" @click="cancelRemoval()" data-dismiss="modal">{{ cancelButtonText }}</button>
                  <button v-if="itemType !== eRemoveItemType.UnauthorisedAction" class="btn btn-danger" id="removeButton" @click="confirmRemoval()">Confirm</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import { rulesEnum } from "@/util/enums/rules";

export default {
    props: {
        itemToRemove: {
            type: Object,
            default: () => ({}),
            required: true
        },
        itemType: {
            type: String,
            default: "",
            required: true
        }
    },
    data() {
        return {
            showConfirmationScreen: false,
            removeSuccessful: false,
            confirmationMessage: "",
            modalTitle: "",
            cancelButtonText: 'Cancel'
        }
    },
    created(){
      $(this.$el).modal({ backdrop: 'static', keyboard: false });
        switch (this.itemType){
            case this.eRemoveItemType.Offer:
                this.modalTitle = "Are you sure you want to remove this offer?";
                break;
            case this.eRemoveItemType.Negotiation:
                this.modalTitle = "Are you sure you want to withdraw this negotiation?";
                break;
            case this.eRemoveItemType.Location:
                this.modalTitle = "Are you sure you want to remove this delivery location?";
                break;
            case this.eRemoveItemType.AdminAccess:
                this.modalTitle = `Remove administrator access for ${this.itemToRemove.firstName} ${this.itemToRemove.lastName}?`;
                break;
            case this.eRemoveItemType.User:
                this.modalTitle = `Delete User`;
                break;
            case this.eRemoveItemType.ActiveUser:
                this.modalTitle = `Disable User`;
                break;
            case this.eRemoveItemType.UnauthorisedAction:
                this.modalTitle = `Unauthorised Action`;
                break;
            case this.eRemoveItemType.Rule:
                this.modalTitle = `Are you sure you would like to remove this rule?`;
                break;
            case this.eRemoveItemType.UnconfirmedChanges:
                this.modalTitle = `Are you sure you want to leave this page?`;
                this.cancelButtonText = "Back";
                break;
            default:
                break;
        }
    },
    computed: {
        ...mapState("itemRemoval", ["eRemoveItemType", "eChangesType"]),
        ...mapState('login', ['userLoggedIn']),
        ...mapGetters('fuelsType', ['getFuelsType']),
        ...mapGetters('metadata', ['networkConfig']),
        ...mapGetters('login', ['isPricingEngineEnabled']),
    },
    methods: {
        ...mapActions('itemRemoval', ['openConfirmationRemoval']),
        ...mapActions('negotiation', ['getNegotiations', 'selectNegotiation']),
        ...mapActions('sellerOffers', ['getAllOffersForUser']),
        ...mapActions('offers', ['getLiveOffers']),
        ...mapActions('deliveryLocations', ['getDeliveryLocations', 'deleteDeliveryLocation']),
        getNetwork: function(){
            switch (this.itemType){
                case this.eRemoveItemType.Offer:
                    return this.itemToRemove.networkId === this.networkConfig.UkFuelsId ? "UK Fuels" : "Keyfuels";
                case this.eRemoveItemType.Negotiation:
                    return this.itemToRemove.fuel_id === this.networkConfig.UkFuelsId ? "UK Fuels" : "Keyfuels";
            }
        },
        getUnitDisplay: function(unitId){
            const unitOfMeasurementEnums = rulesEnum.eUnitType;
            const enumSelection = Object.keys(unitOfMeasurementEnums).reduce((r, k) => r.concat(k, unitOfMeasurementEnums[k]), []);
            const selectedEnum = enumSelection.find(e => e.id === unitId);
            return selectedEnum ? selectedEnum.display : null;
        },
        closeModal: function(preventResettingItemToRemove){
            $(this.$el).modal('hide')
            if (!preventResettingItemToRemove) this.openConfirmationRemoval(false);
        },
        confirmRemoval: async function(){
            let preventResettingItemToRemove = false;

            switch (this.itemType){
                case this.eRemoveItemType.Offer:
                    this.removeOffer();
                    break;
                case this.eRemoveItemType.Negotiation:
                    this.withdrawNegotiation();
                    break;
                case this.eRemoveItemType.Location:
                    this.deleteLocation();
                    break;
                case this.eRemoveItemType.AdminAccess:
                case this.eRemoveItemType.User:
                case this.eRemoveItemType.ActiveUser:
                    await this.$emit("removalConfirmed");
                    break;
                case this.eRemoveItemType.Rule:
                    preventResettingItemToRemove = true;
                    await this.$store.dispatch("rules/confirmRuleRemoval", true);
                    break;
                case this.eRemoveItemType.UnconfirmedChanges:
                    await this.$store.dispatch("itemRemoval/confirmUnsavedChangesRemoval", true);
                    break;
                default:
                    break;
            } 
            
            this.closeModal(preventResettingItemToRemove);
        },
        getParentModal: function(itemType, item){
            const { priority, parentModal } = item;
            switch(itemType){
                case this.eRemoveItemType.Rule:
                    if (priority === rulesEnum.ePriority.Global) return this.$root.$refs.dailyPrices;
                    else if (priority === rulesEnum.ePriority.Offer) return this.$root.$refs.offerModal;
                    break;
                case this.eRemoveItemType.UnconfirmedChanges:
                    if (priority === this.eChangesType.GlobalPricing) return this.$root.$refs.dailyPrices;
                    else if (priority === this.eChangesType.Offer) return this.$root.$refs.offerModal;
                    else if (priority === this.eChangesType.RelationshipManagement) return parentModal;
                    break;
            }
        },
        cancelRemoval: function(){
            switch (this.itemType){
                case this.eRemoveItemType.UnconfirmedChanges: {
                    const parentModal = this.getParentModal(this.itemType, this.itemToRemove);
                    if (parentModal) {
                      const parentModalEl = parentModal.$el;
                      $(parentModalEl).modal({ backdrop: 'static', keyboard: false });
                    }
                    break;
                }
            }

            this.closeModal();
        },
        removeOffer: async function(){
            try {
                const response = await this.axios.put('/api/editOffer',{params: {
                    offerId: this.itemToRemove._id,
                    userId: this.userLoggedIn.userId,
                    statusTypeId: 'REMOVED'
                }})

                this.getAllOffersForUser()
                
                this.removeSuccessful = true
                this.confirmationMessage = 'Offer has been removed'
            } catch(error) {
                console.dir(error);
                this.removeSuccessful = false
                this.confirmationMessage = 'Sorry, we are currently unable to remove your offer. Please try again later'
            }

            this.showConfirmationScreen = true;
        },
        withdrawNegotiation: async function(){
            try {
                const negotiationId = this.itemToRemove._id;
                const response = await this.axios.get(`/api/negotiation/${negotiationId}/withdraw`);

                this.getNegotiations({ status: 'All' }).then(() => {
                    this.selectNegotiation(negotiationId)
                    this.removeSuccessful = true
                    this.confirmationMessage = 'This negotiation has been closed'
                })
            } catch(error) {
                console.dir(error);
                this.removeSuccessful = false
                this.confirmationMessage = 'Sorry, we are currently unable to withdraw your negotiation. Please try again later'
            }

            this.showConfirmationScreen = true 
        },
        deleteLocation: async function(){
            try {
                await this.deleteDeliveryLocation(this.itemToRemove._id)
                /* Let's update the list of delivery locations and bind live offers that match updated list */
                this.getLiveOffers();

                this.removeSuccessful = true;
                this.confirmationMessage = 'Delivery location has been removed';
            } catch(error) {
                console.dir(error);
                this.removeSuccessful = false;
                this.confirmationMessage = 'Sorry, we are currently unable to remove this delivery location. Please try again later';
            }
            this.closeModal();
        }
    }
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/components/_modals.scss";
  .modal{
    z-index: 1051;
  }
</style>
